import {formatCurrency} from '@angular/common';
import {ElementRef, Injectable} from '@angular/core';
import jsPDF from 'jspdf';
import {Styles} from '@fortawesome/fontawesome-svg-core';
import autoTable from 'jspdf-autotable';
import {BenutzerModels} from '../models/benutzer-models';
import {GlobalModels} from '../models/global-models';
import {ObjektModels} from '../models/objekt-models';
import {ServiceAppModels} from '../models/serviceapp-models';
import {SwissInternModels} from '../models/swissintern-models';
import {DatumFormatPipe} from '../pipes/datum-format.pipe';
import {MessageBoxService} from './message-box.service';
import swissFonts from './pdfreport.service.fonts';
import {ServiceAppService} from './service-app.service';
import {SwissSettingsService} from './swiss-settings.service';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {ReportModels} from '../models/report-models';
import {UserAuthenticationService} from './user-authentication.service';

@Injectable({
    providedIn: 'root'
})
export class PDFReportService {
    signatureCanvas?: ElementRef<HTMLCanvasElement>;
    public sa_Config?: ServiceAppModels.SA_Config;
    public isSigned = false;
    public currTermin?: ServiceAppModels.SA_Objekt;
    public currObj?: ObjektModels.ObjektElement;
    public mwstSatz?: string;
    public mitarbeiter = '';
    private sa_PDFRapport: ServiceAppModels.SA_PDFRapport | SwissInternModels.SI_PDFRapport = {
        invoice: undefined, positions: {}, type: ''
    };
    private sa_InvoiceItems?: ServiceAppModels.SA_InvoiceItem[];
    private tableMetaTop: any = null;
    private tableMetaBottom: any = null;
    private currKundenAdresse?: ServiceAppModels.SA_Adress | ObjektModels.ObjektAdresseShort;
    private currLieferAdresse?: ServiceAppModels.SA_Adress;
    private currAnwender?: BenutzerModels.Anwender;
    private currMitarbeiter?: ServiceAppModels.SA_Employees;
    private currMandant?: SwissAppConfigModels.SwissAppMandant;
    private totalPreisExkl = 0.0;
    private totalMwSt = 0.0;
    private totalPreisInkl = 0.0;
    private waehrung = 'CHF';

    constructor(private svcSwissSettings: SwissSettingsService,
                private svcSA: ServiceAppService,
                private userAuthService: UserAuthenticationService,
                private _datumFormatPipe: DatumFormatPipe,
                private svcMessageBox: MessageBoxService) {

        this.initPDFReportService();

        this.sa_Config = this.svcSA.getSA_Config();
        if (this.sa_Config?.waehrungInRapportAnzeigen) {
            this.waehrung = 'CHF';
        } else {
            this.waehrung = '';
        }
    }

    public getColumns = function () {
        return [{title: '', dataKey: 'MiscID'}, {title: '', dataKey: 'MiscValue'},];
    };

    initPDFReportService() {
        this.currAnwender = this.userAuthService.getCurrentAuthenticatedUser();
        this.currMandant = this.userAuthService.getCurrentMandant();
        this.currMitarbeiter = this.svcSA.getAktuellerMitarbeiter();
        if (this.currAnwender) {
            this.mitarbeiter = this.currAnwender!.firstName + ' ' + this.currAnwender!.lastName;
            this.mwstSatz = String((Math.round(this.currAnwender!.sA_PercentageVAT! * 100) / 100).toFixed(2)).replace(',', '.');
        }
    }

    generateRapport(sa_PDFRapport: ServiceAppModels.SA_PDFRapport, currTermin: ServiceAppModels.SA_Objekt, totalPreisExkl: number, totalMwSt: number, totalPreisInkl: number, sigCanvas: ElementRef<HTMLCanvasElement>, isSigned: boolean): Promise<ReportModels.BaseReportResult | undefined> {
        return new Promise<ReportModels.BaseReportResult | undefined>((resolve) => {

            if (!this.currAnwender) {
                this.initPDFReportService();
            }

            this.sa_PDFRapport = sa_PDFRapport;
            this.currTermin = currTermin;
            this.currLieferAdresse = this.svcSA.getAuftragsAdresse(this.currTermin.objectID, GlobalModels.AdressTypes.Lieferant);
            this.currKundenAdresse = this.svcSA.getAuftragsAdresse(this.currTermin.objectID, GlobalModels.AdressTypes.Kunde);
            this.totalPreisExkl = totalPreisExkl;
            this.totalMwSt = totalMwSt;
            this.totalPreisInkl = totalPreisInkl;
            this.signatureCanvas = sigCanvas;
            this.isSigned = isSigned;
            resolve(this.fillReportWithData(sa_PDFRapport.type));
        });
    }


    generateSIRapport(sa_PDFRapport: SwissInternModels.SI_PDFRapport, objectID: string, totalPreisExkl: number, totalMwSt: number, totalPreisInkl: number, sigCanvas: ElementRef<HTMLCanvasElement>, isSigned: boolean, clientAddress: ServiceAppModels.SA_Adress | ObjektModels.ObjektAdresseShort | undefined, obj: ObjektModels.ObjektElement | undefined): Promise<ReportModels.BaseReportResult | undefined> {
        return new Promise<ReportModels.BaseReportResult | undefined>((resolve) => {

            if (!this.currAnwender) {
                this.initPDFReportService();
            }

            this.sa_PDFRapport = sa_PDFRapport;
            this.currKundenAdresse = clientAddress;
            this.totalPreisExkl = totalPreisExkl;
            this.totalMwSt = totalMwSt;
            this.totalPreisInkl = totalPreisInkl;
            this.signatureCanvas = sigCanvas;
            this.isSigned = isSigned;
            this.currObj = obj;
            resolve(this.fillReportWithData(sa_PDFRapport.type));
        });
    }

    fillReportWithData(type: string): Promise<ReportModels.BaseReportResult | undefined> {
        return new Promise<ReportModels.BaseReportResult | undefined>((resolve) => {

            if (this.sa_PDFRapport.invoice) {
                try {
                    if (this.sa_PDFRapport.invoice.template.SA_InvoiceItems) {
                        this.sa_InvoiceItems = this.sa_PDFRapport.invoice.template.SA_InvoiceItems;
                        if (this.sa_InvoiceItems) {
                            this.sa_InvoiceItems.forEach((component) => {
                                // console.log('Neues Draw-Element: ', component.ItemHeader);

                                switch (component.ItemHeader) {
                                    case 'Address':
                                        this.drawAddress(component);
                                        break;
                                    case 'CompanyAddress':
                                        this.drawCompanyAddress(component);
                                        break;
                                    case 'ClientAddress':
                                        this.drawClientAddress(component);
                                        break;
                                    case 'DeliveryAddress':
                                        this.drawDeliveryAddress(component);
                                        break;
                                    case 'Signature':
                                        this.drawSignature(component);
                                        break;
                                    case 'Logo':
                                        this.drawLogo(component);
                                        break;
                                    case 'Table':
                                        // Auftrag / Objekt / Notizen / Lieferadresse / Arbeitsbeschreib / Zusätzliche arbeiten.
                                        autoTable(this.sa_PDFRapport.invoice!.doc, {
                                            columns: this.getColumns(),
                                            body: this.sa_PDFRapport.invoice!.data, margin: {
                                                left: this.sa_PDFRapport.invoice!.MiscTablePos.x,
                                            },
                                            startY: this.sa_PDFRapport.invoice!.MiscTablePos.y, theme: 'plain',
                                            bodyStyles: {minCellHeight: 15, minCellWidth: 100},
                                            tableWidth: 'auto',
                                            styles: {overflow: 'linebreak', fontSize: 9},
                                            columnStyles: {
                                                0: {cellWidth: 100}
                                            }, didParseCell: (data) => {
                                                this.tableMetaTop = data.table;
                                            },
                                        });
                                        // Auftragspositionen
                                        if (this.sa_PDFRapport.invoice!.printPositionsTable) {
                                            this.drawTable(component, this.sa_PDFRapport.type);
                                        }
                                        break;
                                    case 'LogoFuss':
                                        (this.sa_PDFRapport.invoice!.doc as any).footerPos = component;
                                        this.drawFooter(component);
                                        break;
                                    default:
                                        this.drawText(component, type);
                                        break;
                                }
                            });
                        }

                        resolve(this.output(type));
                    }
                } catch (ex) {
                    this.svcMessageBox.showError('Der Rapport konnte nicht erstellt werden. Fehler: ' + ex, '!! FEHLER !!');
                    console.error('Fehler im Erstellen des Rapports: ', ex);
                    resolve(undefined);
                }
            }
        });
    }

    drawAddress(cmp: ServiceAppModels.SA_InvoiceItem) {
        // console.log('Draw address', cmp);
        const addrs = this.sa_PDFRapport.invoice!.address, pos = this.getPosition(cmp), l = cmp.ItemHeight;

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }
        if (addrs.FirstName != '') {
            this.sa_PDFRapport.invoice!.doc.text(addrs.FirstName + ' ' + addrs.LastName, pos.x, pos.y);
        } else {
            this.sa_PDFRapport.invoice!.doc.text(addrs.LastName, pos.x, pos.y);
        }
        this.sa_PDFRapport.invoice!.doc.text(addrs.Street1, pos.x, pos.y + l);
        this.sa_PDFRapport.invoice!.doc.text(addrs.Zip + ' ' + addrs.City, pos.x, pos.y + l * 2);
        this.resetStyle();
    }

    drawCompanyAddress(cmp: ServiceAppModels.SA_InvoiceItem) {
        this.resetStyle();
        // console.log('Draw company address', cmp);

        const addrs = this.sa_PDFRapport.invoice!.mandantAdr, pos = this.getPosition(cmp), l = cmp.ItemHeight;

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        if (addrs) {
            if (addrs.companyName != '') {
                this.sa_PDFRapport.invoice!.doc.text(addrs.companyName, pos.x, pos.y);
            }
            this.sa_PDFRapport.invoice!.doc.text(addrs.street + ' ' + addrs.houseNumber, pos.x, pos.y + Number(l));
            this.sa_PDFRapport.invoice!.doc.text(addrs.zip + ' ' + addrs.city, pos.x, pos.y + Number(l) * 2);
            this.sa_PDFRapport.invoice!.doc.text(addrs.phone, pos.x, pos.y + l * 3);
            this.resetStyle();
        }
    }

    drawClientAddress(cmp: ServiceAppModels.SA_InvoiceItem) {
        const pos = this.getPosition(cmp), l = cmp.ItemHeight;

        console.log('drawClientAddress', this.currKundenAdresse, cmp);

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        if (this.currKundenAdresse) {
            if (this.currKundenAdresse.firstName != '') {
                this.sa_PDFRapport.invoice!.doc.text(this.currKundenAdresse.firstName + ' ' + this.currKundenAdresse.lastName, pos.x, pos.y);
            } else {
                this.sa_PDFRapport.invoice!.doc.text(this.currKundenAdresse.lastName, pos.x, pos.y);
            }
            this.sa_PDFRapport.invoice!.doc.text(this.currKundenAdresse.street1, pos.x, pos.y + Number(l));
            this.sa_PDFRapport.invoice!.doc.text(this.currKundenAdresse.zip + ' ' + this.currKundenAdresse.city, pos.x, pos.y + Number(l) * 2);
            this.resetStyle();
        }
    }

    drawDeliveryAddress(cmp: ServiceAppModels.SA_InvoiceItem) {
        // console.log('Draw deliveryAddress', cmp);

        const pos = this.getPosition(cmp), l = cmp.ItemHeight;

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        if (this.currLieferAdresse) {
            if (this.currLieferAdresse.firstName != '') {
                this.sa_PDFRapport.invoice!.doc.text(this.currLieferAdresse.firstName + ' ' + this.currLieferAdresse.lastName, pos.x, pos.y);
            } else {
                this.sa_PDFRapport.invoice!.doc.text(this.currLieferAdresse.lastName, pos.x, pos.y);
            }
            this.sa_PDFRapport.invoice!.doc.text(this.currLieferAdresse.street1, pos.x, pos.y + l);
            this.sa_PDFRapport.invoice!.doc.text(this.currLieferAdresse.zip + ' ' + this.currLieferAdresse.city, pos.x, pos.y + l * 2);
            this.resetStyle();
        }
    }

    drawSignature(cmp: ServiceAppModels.SA_InvoiceItem) {
        // console.log('Draw signature', cmp);
        const pos = this.getPosition(cmp);
        const w = cmp.ItemWidth;
        const h = cmp.ItemHeight;

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        if (this.signatureCanvas && this.isSigned) {
            this.sa_PDFRapport.invoice!.doc.text(cmp.ItemContent, pos.x, pos.y);
            pos.y += 25;

            const image = (this.signatureCanvas!.nativeElement as HTMLCanvasElement).toDataURL('image/png');
            try {
                this.sa_PDFRapport.invoice!.doc.addImage(image, 'PNG', pos.x, pos.y, Number(w), Number(h));
            } catch (ex) {
                console.error('Fehler beim Hinzufügen eines Bilder', ex);
            }
        }
        // console.log('Ende Draw signature', cmp);
        this.resetStyle();
        // console.log('Ende Draw signature nach resetStyle', cmp);
    }

    drawLogo(cmp: ServiceAppModels.SA_InvoiceItem) {
        // console.log('Draw logo', cmp, this.sa_PDFRapport.invoice!.logo);
        const pos = this.getPosition(cmp), w = cmp.ItemWidth, h = cmp.ItemHeight;

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        if (this.sa_PDFRapport.invoice) {
            this.sa_PDFRapport.invoice!.doc.addImage(this.sa_PDFRapport.invoice!.logo, 'PNG', pos.x, pos.y, Number(w), Number(h));
        }
    }

    drawTable(cmp: ServiceAppModels.SA_InvoiceItem, type: string) {
        const d = this.sa_PDFRapport.invoice!.template.SA_TableData;

        // console.log('Draw table 2', cmp);
        const header = this.table(d).header();
        let body: any;
        if (type === 'Type_SA_Rapport') {
            body = this.table(d).body();
        } else {
            body = this.table(d).bodyIntern();
        }
        const footer = this.table(d).footer();

        body = body.concat(footer); // JS: _.union(body, footer);
        // console.log('header/body/footer', header, body, footer);

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        let font = 'Arial';
        const item = (cmp.style as any).font;
        if (item) {
            font = item;
        }
        // console.log('Draw table vor Aufruf autoTable', cmp);

        autoTable(this.sa_PDFRapport.invoice!.doc, {
            head: [header], body, margin: {}, startY: this.tableMetaTop.finalY + 20, bodyStyles: this.getTableStyle(cmp.style), styles: {overflow: 'linebreak'}, /*
      columnStyles: {
        1: { cellWidth: 'auto', overflow: 'linebreak' },
      },
      */
            headStyles: {
                fillColor: [203, 51, 52],
            }, didParseCell: (data) => {
                this.tableMetaBottom = data.table;
            }, willDrawCell: (data) => {
                // console.log('In willDrawCell', data);
                if (header[data.column.dataKey] === 'Total' || header[data.column.dataKey] === 'Menge' || header[data.column.dataKey] === 'Preis') {
                    data.cell.styles.halign = 'right';
                }
                if (header[data.column.dataKey] === 'Total' || header[data.column.dataKey] === 'Preis') {
                    if (!this.currAnwender!.sA_ShowPrices) {
                        data.cell.text = [];
                    }
                }

                if (this.currAnwender!.sA_ShowPrices) {
                    // Preise dürfen angedruckt werden
                    const rows = data.table.body; // Anzahl zeilen ermitteln
                    // letzte 3 Zeilen Fett machen
                    if ([rows.length - 1, rows.length - 2, rows.length - 3].indexOf(data.row.index) != -1) {
                        this.sa_PDFRapport.invoice!.doc.setFont(font, 'bold');
                    }
                    // letzte Zeile Violett einfärben
                    if (data.row.index == rows.length - 1) {
                        this.sa_PDFRapport.invoice!.doc.setFillColor(200, 200, 255);
                    }
                }
            },
        });

        this.resetStyle();
    }

    drawFooter(cmp: ServiceAppModels.SA_InvoiceItem) {
        // console.log('Draw Footer logo', cmp);

        const pos = this.getPosition(cmp), w = cmp.ItemWidth, h = cmp.ItemHeight;

        // console.log('Draw Footer logo 1');

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }
        // console.log('Draw Footer logo 2', this.sa_PDFRapport.invoice!.doc.internal.pageSize.height );

        this.sa_PDFRapport.invoice!.doc.addImage(this.sa_PDFRapport.invoice!.logoFooter, 'PNG', pos.x, this.sa_PDFRapport.invoice!.doc.internal.pageSize.height - pos.y, Number(w), Number(h));
        // console.log('Draw Footer logo 3');
    }

    drawText(cmp: ServiceAppModels.SA_InvoiceItem, type: string) {
        if (type === 'Type_SA_Rapport') {
            this.drawSAText(cmp);
        } else {
            this.drawSIText(cmp);
        }
    }

    htmlDecode(input: string) {
        const doc = new DOMParser().parseFromString(input, 'text/html');
        const innerElemement = doc.documentElement.getElementsByTagName('body')[0].firstChild;
        let t = '';
        if (innerElemement) {
            innerElemement.childNodes.forEach(node => {
                t += node.textContent + '\n';
            });
        }
        return t;
    }

    drawSAText(cmp: ServiceAppModels.SA_InvoiceItem) {

        // console.log('Draw text', cmp);

        const pos = this.getPosition(cmp);

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }

        let fixedtext: any;
        if (cmp.ItemContent.indexOf('{{') >= 0) {
            const h = cmp.ItemContent.split('{{');
            fixedtext = h[0].trim();
            let variablefield = h[1].trim();
            if (variablefield.indexOf(',') > 0) {
                variablefield.split(',').forEach((c) => {
                    if (c === 'ObjDescription}}') {
                        if (this.currTermin) {
                            this.sa_PDFRapport.invoice!.data.push({
                                MiscID: fixedtext, MiscValue: this.currTermin.objectNumber + ' ' + this.currTermin.objDescription,
                            });
                            fixedtext = '';
                        }
                    } else if (c === 'SA_Ort') {
                        if (this.currTermin) {
                            if (this.currMandant) {
                                fixedtext = this.currMandant.city;
                            }
                        }
                    } else if (c === 'CreatedDate}}') {
                        if (this.currTermin) {
                            const seldoc: ServiceAppModels.SA_Document[] = [];
                            this.svcSA
                                .getDBTerminDokumente()
                                .filter((d) => d.fK_ServiceID === this.currTermin!.serviceID)
                                .forEach((terminDocument) => {
                                    const document = this.svcSA
                                        .getDBObjektDokumente()
                                        .filter((d) => d.documentID === terminDocument.documentID)[0];
                                    if (document.fileName === 'Lieferschein.pdf') {
                                        seldoc.push(document);
                                    }
                                });

                            let formatted = '';

                            // Wenn der Lieferschein bereits existiert
                            if (seldoc.length === 1) {
                                if (seldoc[0].createdDate) {
                                    const date = new Date(seldoc[0].createdDate!);
                                    formatted = this._datumFormatPipe.transformOnlyDate(date);
                                }
                            } else {
                                // Der Lieferschein existiert nicht
                                const date = new Date();
                                formatted = this._datumFormatPipe.transformOnlyDate(date);
                            }
                            fixedtext = fixedtext + ', ' + formatted;
                        }
                    }
                });
            } else {
                variablefield = variablefield.substring(0, variablefield.length - 2);
                if (variablefield === 'ObjectNumber') {
                    if (this.currTermin) {
                        fixedtext = fixedtext + ' ' + this.currTermin.objectNumber;
                    }
                } else if (variablefield === 'Note4') {
                    this.sa_PDFRapport.invoice!.MiscTablePos = pos;
                    if (this.currTermin) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.currTermin.note4,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'ManufacturingDescription') {
                    if (this.currTermin) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.htmlDecode(this.currTermin.manufacturingDescription),
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'Memo') {
                    if (this.currTermin) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.htmlDecode(this.currTermin.memo),
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'ClientAddress') {
                    if (this.currTermin) {
                        let clientAdr = '';

                        if (this.currKundenAdresse) {
                            if (this.currKundenAdresse.firstName != '') {
                                clientAdr = this.currKundenAdresse.firstName + ' ' + this.currKundenAdresse.lastName;
                            } else {
                                clientAdr = this.currKundenAdresse.lastName;
                            }
                            clientAdr += '\r' + this.currKundenAdresse.street1;
                            clientAdr += '\r' + this.currKundenAdresse.zip + ' ' + this.currKundenAdresse.city;
                        }

                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: clientAdr,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'DeliveryAddress') {
                    if (this.currTermin) {
                        let deliveryAdr = '';

                        if (this.currLieferAdresse) {
                            if (this.currLieferAdresse.firstName != '') {
                                deliveryAdr = this.currLieferAdresse.firstName + ' ' + this.currLieferAdresse.lastName;
                            } else {
                                deliveryAdr = this.currLieferAdresse.lastName;
                            }
                            deliveryAdr += '\r' + this.currLieferAdresse.street1;
                            deliveryAdr += '\r' + this.currLieferAdresse.zip + ' ' + this.currLieferAdresse.city;
                        }

                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: deliveryAdr,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'AdditionalWork') {
                    if (this.currTermin) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.currTermin.additionalWork,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'EmployeeName') {
                    if (this.currMitarbeiter) {
                        fixedtext = fixedtext + ' ' + this.currMitarbeiter.firstName + ' ' + this.currMitarbeiter.lastName + '.';
                    }
                } else if (variablefield === 'SA_MwST') {
                    if (this.currMandant) {
                        fixedtext = this.currMandant.vatNumber;
                    }
                }
            }
        } else {
            fixedtext = cmp.ItemContent;
        }
        if (fixedtext !== '') {
            const text = this.sa_PDFRapport.invoice!.doc.splitTextToSize(fixedtext, cmp.ItemWidth);

            this.sa_PDFRapport.invoice!.doc.text(text, pos.x, pos.y);

            this.resetStyle();
        }
    }

    drawSIText(cmp: ServiceAppModels.SA_InvoiceItem) {

        // console.log('Draw text', cmp);

        const pos = this.getPosition(cmp);

        if (cmp.style) {
            this.applyStyles(cmp.style);
        }
        let fixedtext: any;
        if (cmp.ItemContent.indexOf('{{') >= 0) {
            const h = cmp.ItemContent.split('{{');
            fixedtext = h[0].trim();
            let variablefield = h[1].trim();
            if (variablefield.indexOf(',') > 0) {
                variablefield.split(',').forEach((c) => {
                    if (c === 'ObjDescription}}') {
                        if (this.currObj) {
                            this.sa_PDFRapport.invoice!.data.push({
                                MiscID: fixedtext, MiscValue: this.currObj.objectNumber + ' ' + this.currObj.description,
                            });
                            fixedtext = '';
                        }
                    } else if (c === 'SA_Ort') {
                        if (this.currObj) {
                            if (this.currMandant) {
                                fixedtext = this.currMandant.city;
                            }
                        }
                    } else if (c === 'CreatedDate}}') {
                        if (this.currObj) {
                            const date = new Date();
                            const formatted = this._datumFormatPipe.transformOnlyDate(date);
                            fixedtext = fixedtext + ', ' + formatted;
                        }
                    }
                });
            } else {
                variablefield = variablefield.substring(0, variablefield.length - 2);
                if (variablefield === 'ObjectNumber') {
                    if (this.currObj) {
                        fixedtext = fixedtext + ' ' + this.currObj.objectNumber;
                    }
                } else if (variablefield === 'Note4') {
                    this.sa_PDFRapport.invoice!.MiscTablePos = pos;
                    if (this.currObj) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.currObj.note4,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'ManufacturingDescription') {
                    if (this.currObj) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.htmlDecode(this.currObj.manufacturingDescription!),
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'Memo') {
                    if (this.currObj) {
                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: this.htmlDecode(this.currObj.memo!),
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'ClientAddress') {
                    if (this.currObj) {
                        let clientAdr = '';

                        if (this.currKundenAdresse) {
                            if (this.currKundenAdresse.firstName != '') {
                                clientAdr = this.currKundenAdresse.firstName + ' ' + this.currKundenAdresse.lastName;
                            } else {
                                clientAdr = this.currKundenAdresse.lastName;
                            }
                            clientAdr += '\r' + this.currKundenAdresse.street1;
                            clientAdr += '\r' + this.currKundenAdresse.zip + ' ' + this.currKundenAdresse.city;
                        }

                        this.sa_PDFRapport.invoice!.data.push({
                            MiscID: fixedtext, MiscValue: clientAdr,
                        });
                        fixedtext = '';
                    }
                } else if (variablefield === 'EmployeeName') {
                    if (this.currMitarbeiter) {
                        fixedtext = fixedtext + ' ' + this.currMitarbeiter.firstName + ' ' + this.currMitarbeiter.lastName + '.';
                    }
                } else if (variablefield === 'SA_MwST') {
                    if (this.currMandant) {
                        fixedtext = this.currMandant.vatNumber;
                    }
                }
            }
        } else {
            fixedtext = cmp.ItemContent;
        }
        if (fixedtext !== '') {
            const text = this.sa_PDFRapport.invoice!.doc.splitTextToSize(fixedtext, cmp.ItemWidth);

            this.sa_PDFRapport.invoice!.doc.text(text, pos.x, pos.y);

            this.resetStyle();
        }
    }

    output(type: string): Promise<ReportModels.BaseReportResult> {
        return new Promise((resolve) => {
            if (this.sa_PDFRapport.invoice) {
                const binaryPDF = new Blob([this.sa_PDFRapport.invoice.doc.output('blob')], {type: 'application/pdf'});

                // Service-App Rapport
                if (type === 'Type_SA_Rapport') {
                    const reportResult: ReportModels.ServiceReportResult = {
                        serviceID: this.currTermin!.serviceID, objectID: this.currTermin!.objectID, name: this.sa_PDFRapport.invoice.name, binary: binaryPDF
                    };
                    resolve(reportResult);
                } else {
                    // Objekt Rapport
                    const reportResult: ReportModels.BaseReportResult = {
                        objectID: this.currObj!.objectID, name: this.sa_PDFRapport.invoice.name, binary: binaryPDF
                    };
                    resolve(reportResult);
                }
            }
        });
    }

    getPosition(cmp: ServiceAppModels.SA_InvoiceItem) {
        let header = '';
        // console.log('Start GetPosition', cmp);
        let headers, relative, pos = {
            x: parseInt(cmp.ItemX), y: parseInt(cmp.ItemY), w: cmp.ItemWidth, h: cmp.ItemHeight,
        };

        if (cmp.Position.toLowerCase() === 'absolute') {
            this.sa_PDFRapport.positions[cmp.ItemHeader as any] = pos;
            return pos;
        } else {
            headers = cmp.Position.split(':');
            if (headers.length > 1) {
                header = headers[1];
            }
        }

        if (header) {
            relative = this.sa_PDFRapport.positions[header];
        }

        if (!relative) {
            this.sa_PDFRapport.positions[cmp.ItemHeader] = pos;

        }

        if (header === 'Address' || header === 'ClientAddress' || header === 'DeliveryAddress') {
            if (pos.x > 0) {
                pos.x += relative.x + relative.w;
                pos.y += relative.y + relative.h * 3;
            } else {
                pos.x += relative.x;
                pos.y += relative.y + relative.h * 3;
            }
        } else if (header === 'Table') {
            if (this.tableMetaBottom.finalY + pos.y >= this.sa_PDFRapport.invoice!.doc.internal.pageSize.height - 60) {
                pos.y += pos.y + 50;
                if (!this.sa_PDFRapport.invoice!.relativepageadded) {
                    this.sa_PDFRapport.invoice!.doc.addPage();
                    this.sa_PDFRapport.invoice!.relativepageadded = true;
                }
            } else {
                pos.y = pos.y + this.tableMetaBottom.finalY;
            }
        } else if (header === 'Misc4') {
            pos.y += this.tableMetaTop.finalY;
        } else {
            if (pos.x > 0) {
                console.log('X > 0', pos, relative);
                if (this.tableMetaBottom) // Dieser Wert ist nur gesetzt, wenn die PositionsTabelle angedruckt wurde
                {
                    pos.y += this.tableMetaBottom.finalY;
                } else {
                    pos.y += relative.y + Number(relative.h);
                }
            } else {
                pos.x = relative.x;
                pos.y += relative.y + Number(relative.h);
            }
        }

        this.sa_PDFRapport.positions[cmp.ItemHeader] = pos;

        return pos;
    }

    applyStyles(styleRules: any) {
        let fontType = 'normal';
        const item = styleRules.fontType;
        if (item && item.toLowerCase() === 'bold') {
            fontType = 'bold';
        }

        for (const style in styleRules) {
            if (styleRules.hasOwnProperty(style)) {
                switch (style) {
                    case 'font':
                        this.sa_PDFRapport.invoice!.doc.setFont(styleRules[style].toLowerCase(), fontType);
                        break;
                    case 'fontSize':
                        this.sa_PDFRapport.invoice!.doc.setFontSize(parseInt(styleRules[style]));
                        break;
                    case 'color':
                        this.sa_PDFRapport.invoice!.doc.setTextColor(styleRules[style][0], styleRules[style][1], styleRules[style][2]);
                        break;
                }
            }
        }
    }

    resetStyle() {
        this.sa_PDFRapport.invoice!.doc.setFont('arial');
        this.sa_PDFRapport.invoice!.doc.setFontSize(14);
        this.sa_PDFRapport.invoice!.doc.setTextColor(0, 0, 0);
    }

    table(data: any[]) {
        return {
            header: () => {
                const th: any = [];

                data.forEach((td) => {
                    th.push(td.FieldDisplayName);
                });

                return th;
            },

            body: () => {
                const tr: any = [];
                this.sa_PDFRapport.invoice!.rows.forEach((row) => {
                    const td: any = [];

                    data.forEach((col) => {
                        switch (col.FieldDBField) {
                            case 'EntryDate': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).datum);
                                break;
                            }
                            case 'FK_EmployeeNo': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).mitarbeiter);
                                break;
                            }
                            case 'FK_ProductNo': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).artikel);
                                break;
                            }
                            case 'ShortText': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).bezeichnung);
                                break;
                            }
                            case 'Amount': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).menge);
                                break;
                            }
                            case 'TotalAmount': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).menge);
                                break;
                            }
                            case 'Unit': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).einheit);
                                break;
                            }
                            case 'Total': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).preisTotal);
                                break;
                            }
                            case 'Saleprice': {
                                td.push((row as ServiceAppModels.SA_RapportZeile).preis);
                                break;
                            }
                            default: {
                                td.push('');
                                break;
                            }
                        }
                    });

                    tr.push(td);
                });

                return tr;
            },

            bodyIntern: () => {
                const tr: any = [];
                this.sa_PDFRapport.invoice!.rows.forEach((row) => {
                    const td: any = [];

                    data.forEach((col) => {
                        switch (col.FieldDBField) {
                            case 'EntryDate': {
                                td.push((row as ObjektModels.ObjektRapportPosition).datum);
                                break;
                            }
                            case 'FK_EmployeeNo': {
                                td.push((row as ObjektModels.ObjektRapportPosition).mitarbeiter);
                                break;
                            }
                            case 'ShortText': {
                                td.push((row as ObjektModels.ObjektRapportPosition).arbeitsbezeichnung);
                                break;
                            }
                            case 'Amount': {
                                td.push((row as ObjektModels.ObjektRapportPosition).aufwandInStunden);
                                break;
                            }
                            case 'Unit': {
                                td.push('h');
                                break;
                            }
                            case 'Total': {
                                td.push((row as ObjektModels.ObjektRapportPosition).totalStr);
                                break;
                            }
                            case 'Saleprice': {
                                td.push((row as ObjektModels.ObjektRapportPosition).preisStr);
                                break;
                            }
                            default: {
                                td.push('');
                                break;
                            }
                        }
                    });

                    tr.push(td);
                });

                return tr;
            },

            footer: () => {
                const tr: any = [];
                /* Totalisierungen andrucken */

                if (this.currAnwender!.sA_ShowPrices) {
                    let td: any = [];

                    // Total exkl.
                    data.forEach((col) => {
                        switch (col.FieldDBField) {
                            case 'ShortText': {
                                td.push('Total');
                                break;
                            }
                            case 'Total': {
                                td.push(formatCurrency(this.totalPreisExkl, 'de-CH', this.waehrung, '1.2-2').replace(',', '.'));
                                break;
                            }
                            default: {
                                td.push('');
                                break;
                            }
                        }
                    });
                    tr.push(td);

                    td = [];
                    // MwSt
                    data.forEach((col) => {
                        switch (col.FieldDBField) {
                            case 'ShortText': {
                                td.push('Zuzüglich ' + this.mwstSatz + ' % MwSt.');
                                break;
                            }
                            case 'Total': {
                                td.push(formatCurrency(this.totalMwSt, 'de-CH', this.waehrung, '1.2-2').replace(',', '.'));
                                break;
                            }
                            default: {
                                td.push('');
                                break;
                            }
                        }
                    });
                    tr.push(td);

                    td = [];
                    // Total inkl. MwSt
                    data.forEach((col) => {
                        switch (col.FieldDBField) {
                            case 'ShortText': {
                                td.push('Total inkl. ' + this.mwstSatz + ' % Mehrwertsteuer');
                                break;
                            }
                            case 'Total': {
                                td.push(formatCurrency(this.totalPreisInkl, 'de-CH', this.waehrung, '1.2-2').replace(',', '.'));
                                break;
                            }
                            default: {
                                td.push('');
                                break;
                            }
                        }
                    });
                    tr.push(td);
                }
                return tr;
            },
        };
    }

    AddFontArial(doc: jsPDF) {
        try {
            (doc as any).addFileToVFS('arial-normal.ttf', swissFonts.arialNormal);
            doc.addFont('arial-normal.ttf', 'arial', 'normal');
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('Arial erfolgreich importiert.');
            }
        } catch (ex) {
            console.error('Fehler beim Import der Schriftart Arial.', ex);
        }
    }

    AddFontHelveticaBold(doc: jsPDF) {
        try {
            (doc as any).addFileToVFS('Helvetica-Bold-bold.ttf', swissFonts.helveticaBold);
            doc.addFont('Helvetica-Bold-bold.ttf', 'arial', 'bold');
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('Helvetica erfolgreich importiert.');
            }
        } catch (ex) {
            console.error('Fehler beim Import der Schriftart Helvetica.', ex);
        }
    }

    private getTableStyle(styleRules: string[]): Partial<Styles> {
        let tableStyles: Styles;
        tableStyles = {minCellHeight: '20'};

        for (const style in styleRules) {
            if (styleRules.hasOwnProperty(style)) {
                switch (style) {
                    case 'font':
                        tableStyles.font = styleRules[style].toLowerCase();
                        break;
                    case 'fontSize':
                        tableStyles.fontSize = styleRules[style];
                        break;
                    case 'color':
                        break;
                }
            }
        }
        return tableStyles;
    }
}
